import { ReactNode, useState } from "react";
import { Mui, useTheme } from "@leap4/frontend";
import { AppShellSidebar, SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_EXPANDED_WIDTH } from "./AppShellSidebar";
import { AppShellTopBar } from "./AppShellTopBar";
import { useLocalStorage } from "./useLocalStorage";

const { Box, Link, useMediaQuery } = Mui;

const SkipToContent = () => (
    <Link
        href="#main-content"
        sx={{
            position: "absolute",
            left: "-9999px",
            top: "auto",
            "&:focus": { position: "fixed", top: 16, left: 16, zIndex: 10000, bgcolor: "background.paper", p: 2, borderRadius: 1, boxShadow: 3 },
        }}
    >
        Skip to main content
    </Link>
);

/** The DSP's AppShell: dark sidebar, slim top bar, one scrolling main area. */
export const AppShell = ({ email, children }: { email: string; children: ReactNode }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [collapsed, setCollapsed] = useLocalStorage("sidebar-collapsed", false);
    const [mobileOpen, setMobileOpen] = useState(false);

    const width = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH;

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
            <SkipToContent />
            <AppShellSidebar
                collapsed={collapsed}
                onToggle={() => setCollapsed(!collapsed)}
                mobileOpen={mobileOpen}
                onMobileClose={() => setMobileOpen(false)}
                isMobile={isMobile}
                width={width}
            />
            <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
                <AppShellTopBar onMenuClick={() => setMobileOpen(true)} isMobile={isMobile} email={email} />
                <Box component="main" id="main-content" tabIndex={-1} sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, minWidth: 0, outline: "none" }}>
                    {children}
                </Box>
            </Box>
        </Box>
    );
};
